import i18n from "i18next";
import { config } from "../config";
import { CORRELATION_ID_HEADER } from "../utils";
import { HttpError, HttpErrorKind } from "./httpError";

// Lives in api/ rather than api/queries/ because it is not a hook and has no
// server state: it observes the seam every hook already passes through.
// The first non-hook module here, so the placement is a precedent.

const ENDPOINT = "client-event";

// The endpoint's own bounds. Exceeding one is a 400, so a payload that would
// be refused is trimmed rather than sent to be rejected.
const MAX_ROUTE_LENGTH = 256;
const MAX_CODE_LENGTH = 64;
const MAX_CORRELATION_LENGTH = 128;

// POST /client-event accepts exactly these seven fields and REJECTS any other.
// Built by naming them one at a time: a delete-list over the error object
// starts leaking the moment HttpError gains a field, a positive list does not.
type ClientEvent = {
  code: string;
  status: number;
  correlation_id: string;
  route: string;
  locale: "en" | "fr";
  kind: HttpErrorKind;
  occurred_at: string;
};

const withoutQuery = (value: string): string => value.split("?")[0];

// The failed URL is not safe to send. A student search carries the matricule
// and the name as query parameters, and ids sit in the path.
const pathOf = (url: string): string => {
  const base = config.REACT_APP_PROXY_BASE_URL;

  if (base && url.startsWith(base)) {
    return withoutQuery(url.slice(base.length)) || "/";
  }

  try {
    return new URL(url).pathname;
  } catch {
    return withoutQuery(url);
  }
};

// Every static segment the portal's own endpoints are built from. A segment
// outside this set is an identifier and is masked.
//
// ⛔ Positive, like the field list, and for the same reason. Masking whatever
// "looks like" an id is the inverse test and it fails open: the customer code
// is free text the payer types, it sits in the PATH, and one with no digit in
// it would travel verbatim into the log index.
const STATIC_SEGMENTS = new Set([
  "attested-document",
  "by-url",
  "catalogue",
  "client-event",
  "context",
  "corporate-group",
  "corporate-service",
  "corporate-subsidiary",
  "customer",
  "fee-assignment",
  "logo",
  "pay",
  "payment",
  "payment-method",
  "portal",
  "price",
  "receipt",
  "schoolfee",
  "search",
  "student",
  "theme",
  "token",
]);

// A route, not a URL. An unrecognised segment degrades to :id rather than
// travelling -- a new endpoint reports less precisely, which is the safe
// direction to fail. The tenant is recoverable from the server's own line for
// the same correlation id, which is what the join exists for.
const routeOf = (url: string): string =>
  pathOf(url)
    .split("/")
    .map(segment =>
      segment === "" || STATIC_SEGMENTS.has(segment) ? segment : ":id"
    )
    .join("/")
    .slice(0, MAX_ROUTE_LENGTH);

// The dashboard splits error volume by locale, so the value has to be one of
// the two the endpoint accepts rather than whatever tag i18next resolved.
const localeOf = (): "en" | "fr" =>
  (i18n.language ?? "").toLowerCase().startsWith("fr") ? "fr" : "en";

// Fire and forget. Never awaited on a render path, never throws into a caller,
// never retries: errors reporting errors must not become a second failure on a
// payment surface.
export const reportClientEvent = async (error: HttpError): Promise<void> => {
  // ⛔ The WHOLE body is guarded, not just the request. The caller does not
  // await this, so anything thrown before the fetch would surface as an
  // unhandled rejection -- and reading localStorage does throw where storage
  // is partitioned or blocked.
  try {
    if (!error.correlationId) return;

    const correlationId = error.correlationId.slice(0, MAX_CORRELATION_LENGTH);

    const event: ClientEvent = {
      code: (error.code ?? "").slice(0, MAX_CODE_LENGTH),
      status: error.status,
      correlation_id: correlationId,
      route: routeOf(error.url),
      locale: localeOf(),
      kind: error.kind,
      occurred_at: new Date().toISOString(),
    };

    // A bare fetch, never the seam this reports from. A POST through request()
    // would emit on its own failure and recurse without bound in the payer's
    // browser, exactly while /client-event is unreachable.
    await fetch(`${config.REACT_APP_PROXY_BASE_URL}/${ENDPOINT}`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        [CORRELATION_ID_HEADER]: correlationId,
      },
      body: JSON.stringify(event),
      // Survives the payer navigating away from the failed screen.
      keepalive: true,
    });
  } catch {
    // Swallowed deliberately; see the contract above.
  }
};

export default reportClientEvent;
